import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "notistack";

import { useOrganization, useSetOrganizationDisabled } from "@app/api/organizations";
import { extractErrorMessage } from "@app/api/client";
import { ErrorMessage, Loading } from "@app/components/Feedback";

export default function OrganizationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const orgId = Number(id);

  const { data: org, isLoading, error } = useOrganization(orgId);
  const setDisabled = useSetOrganizationDisabled();

  const onToggle = async (): Promise<void> => {
    if (!org) return;
    const disable = !org.disabledAt;
    const verb = disable ? "disable" : "enable";
    if (!window.confirm(`Are you sure you want to ${verb} "${org.name}"?`)) return;
    try {
      await setDisabled.mutateAsync({ id: org.id, disabled: disable });
      enqueueSnackbar(`Organization ${verb}d`, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err, `Failed to ${verb} organization`), { variant: "error" });
    }
  };

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/organizations")} sx={{ mb: 2 }}>
        Back to organizations
      </Button>

      {isLoading && <Loading />}
      {error && <ErrorMessage error={error} />}

      {org && (
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h5">{org.name}</Typography>
                  <Typography color="text.secondary">
                    {org.type} · Owner: {org.ownerEmail ?? "—"}
                  </Typography>
                  <Chip
                    size="small"
                    sx={{ mt: 1 }}
                    label={org.disabledAt ? "Disabled" : "Active"}
                    color={org.disabledAt ? "default" : "success"}
                  />
                </Box>
                <Button
                  variant="contained"
                  color={org.disabledAt ? "success" : "error"}
                  onClick={onToggle}
                  disabled={setDisabled.isPending}
                >
                  {org.disabledAt ? "Enable organization" : "Disable organization"}
                </Button>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Created {new Date(org.createdAt).toLocaleString()}
                {org.featureBundleId && ` · Plan: ${org.featureBundleId}`}
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Members ({org.members.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {org.members.map((m) => (
                    <TableRow key={m.userId} hover>
                      <TableCell>
                        <Link component="button" onClick={() => navigate(`/users/${m.userId}`)}>
                          {m.email}
                        </Link>
                      </TableCell>
                      <TableCell>{m.username}</TableCell>
                      <TableCell>{m.role}</TableCell>
                      <TableCell>{m.status}</TableCell>
                    </TableRow>
                  ))}
                  {org.members.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No members
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
}
